/**
 * Binary codecs for WebAuthn wire values.
 *
 * WebAuthn works in ArrayBuffers: challenges, credential ids, and assertion
 * parts are all raw bytes. The gateway speaks a few text encodings for those
 * bytes: a challenge arrives as hex, a credential id is base64url, and the
 * assertion's authenticator data and signature are returned as hex. These
 * helpers convert between the browser's ArrayBuffer and those text forms.
 *
 * base64url here is the unpadded URL-safe alphabet WebAuthn uses for credential
 * ids, matching what the gateway stores. Kept in its own module with no DOM
 * dependency so the conversions can be unit tested in a plain Node environment,
 * where the rest of the WebAuthn flow cannot run.
 */

/**
 * Encode bytes as unpadded base64url. Produces the URL-safe alphabet with the
 * trailing padding removed, which is the form WebAuthn credential ids and the
 * gateway both use.
 *
 * @param bytes The bytes to encode, as a Uint8Array or ArrayBuffer.
 * @returns The unpadded base64url string.
 */
export function bytesToBase64Url(bytes: Uint8Array | ArrayBuffer): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  // Build the binary string in chunks so a large input does not overflow the
  // argument list of String.fromCharCode.
  const chunk = 0x8000;
  for (let i = 0; i < view.length; i += chunk) {
    binary += String.fromCharCode(...view.subarray(i, i + chunk));
  }
  const standard = btoa(binary);
  return standard.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decode an unpadded or padded base64url string to bytes. Translates the
 * URL-safe alphabet back to standard base64 and restores the padding before
 * decoding, so a value produced by {@link bytesToBase64Url} round-trips and a
 * padded value from another source is still accepted.
 *
 * @param input The base64url string.
 * @returns The decoded bytes.
 */
export function base64UrlToBytes(input: string): Uint8Array<ArrayBuffer> {
  const standard = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = standard.length % 4 === 0 ? '' : '='.repeat(4 - (standard.length % 4));
  const binary = atob(standard + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Encode bytes as lowercase hex with no prefix. The gateway's assertion verifier
 * reads authenticator_data and signature as hex, so the assertion stamp carries
 * those two parts in this form rather than base64.
 *
 * @param bytes The bytes to encode, as a Uint8Array or ArrayBuffer.
 * @returns The lowercase hex string, with no leading 0x.
 */
export function bytesToHex(bytes: Uint8Array | ArrayBuffer): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let out = '';
  for (let i = 0; i < view.length; i += 1) {
    out += view[i].toString(16).padStart(2, '0');
  }
  return out;
}

/**
 * Decode a hex string to bytes. The gateway sends a WebAuthn challenge as hex,
 * so this turns it into the ArrayBuffer the browser signs over. An odd-length or
 * non-hex input throws rather than silently truncating, so a malformed challenge
 * fails closed instead of being signed in a corrupted form.
 *
 * @param input The hex string, with or without a leading 0x.
 * @returns The decoded bytes.
 * @throws {Error} When the input is not valid even-length hex.
 */
export function hexToBytes(input: string): Uint8Array<ArrayBuffer> {
  const hex = input.startsWith('0x') || input.startsWith('0X') ? input.slice(2) : input;
  if (hex.length % 2 !== 0) throw new Error('hex string has an odd length');
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    const byte = Number.parseInt(hex.substr(i * 2, 2), 16);
    if (Number.isNaN(byte)) throw new Error('hex string carries a non-hex character');
    bytes[i] = byte;
  }
  return bytes;
}
